import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import CameraFileInput from '../components/CameraFileInput';
import ProtectedMediaThumbnail from '../components/ProtectedMediaThumbnail';
import SignatureCanvas from 'react-signature-canvas';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { buildAppDateTimeIso, toAppDateInputValue, toAppTimeInputValue } from '../lib/appDateTime';
import { openProtectedFile } from '../lib/protectedFiles';
import { Trash2 } from 'lucide-react';

const CREATE_NEW_VALUE = '__create_new__';
const DEFAULT_ORGAN_OPTIONS = ['Guarda Ambiental', 'Guarda-Parque', 'PMP', 'Popular'];
const DEFAULT_ANIMAL_GROUP_OPTIONS = ['AVE', 'MAM', 'REPT'];
const DEFAULT_CONSCIOUSNESS_OPTIONS = ['Alerta', 'Letárgico', 'Inconsciente'];
const DEFAULT_HYDRATION_OPTIONS = ['Normal', 'Levemente desidratado', 'Desidratação moderada', 'Desidratação severa', 'Hiperidratado'];
const DEFAULT_ALLOCATION_OPTIONS = ['Internação 1', 'Internação 2', 'Quarentena'];

const toDateInputValue = (dateValue = new Date()) => {
  return toAppDateInputValue(dateValue);
};

const toTimeInputValue = (dateValue = new Date()) => {
  return toAppTimeInputValue(dateValue);
};

const buildDateTimeIso = (dateValue, timeValue) => {
  return buildAppDateTimeIso(dateValue, timeValue);
};

const mergeCustomOption = (options, currentValue) => {
  if (!currentValue || options.includes(currentValue)) {
    return options;
  }
  return [...options, currentValue];
};

const normalizeOptionText = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const getApiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((err) => {
        const location = Array.isArray(err.loc) ? err.loc.join('.') : '';
        return [location, err.msg].filter(Boolean).join(': ');
      })
      .join(', ');
  }
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return fallback;
};

const isFilled = (value) => String(value ?? '').trim().length > 0;

export default function AdmissionForm() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [bases, setBases] = useState([]);
  const [speciesCatalog, setSpeciesCatalog] = useState([]);
  const [admission, setAdmission] = useState(null);
  const [currentTab, setCurrentTab] = useState('identification');

  const signatureRef = useRef();
  const [useCanvas, setUseCanvas] = useState(true);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const [currentAdmissionId, setCurrentAdmissionId] = useState(id || null);
  const canCorrectAdmissionAdminFields = ['administrador', 'programador'].includes(user?.role);
  const canManageSpeciesCatalog = ['administrador', 'programador'].includes(user?.role);

  // Form state
  const [baseId, setBaseId] = useState('');
  const [originCity, setOriginCity] = useState('');
  const [originState, setOriginState] = useState('');
  const [originRegion, setOriginRegion] = useState('');
  const [originNeighborhood, setOriginNeighborhood] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [responsibleOrgan, setResponsibleOrgan] = useState('');
  const [responsibleCity, setResponsibleCity] = useState('');

  // Animal
  const [ibamaId, setIbamaId] = useState('');
  const [admissionDate, setAdmissionDate] = useState(toDateInputValue());
  const [admissionTime, setAdmissionTime] = useState(toTimeInputValue());
  const [originalAdmissionDate, setOriginalAdmissionDate] = useState('');
  const [originalAdmissionTime, setOriginalAdmissionTime] = useState('');
  const [admissionDateCorrectionJustification, setAdmissionDateCorrectionJustification] = useState('');
  const [animalGroup, setAnimalGroup] = useState('');
  const [species, setSpecies] = useState('');
  const [commonName, setCommonName] = useState('');
  const [animalClass, setAnimalClass] = useState('');
  const [pmpId, setPmpId] = useState('');
  const [admittedDead, setAdmittedDead] = useState(false);
  const [ibamaPreview, setIbamaPreview] = useState(null);
  const [loadingIbamaPreview, setLoadingIbamaPreview] = useState(false);
  const [selectedReusableIbamaId, setSelectedReusableIbamaId] = useState('');

  // Clinical
  const [consciousness, setConsciousness] = useState('');
  const [cardiacAuscultation, setCardiacAuscultation] = useState('');
  const [respiratoryAuscultation, setRespiratoryAuscultation] = useState('');
  const [reflexes, setReflexes] = useState('');
  const [temperature, setTemperature] = useState('');
  const [bodyScore, setBodyScore] = useState('');
  const [hydration, setHydration] = useState('');
  const [oilContamination, setOilContamination] = useState(false);
  const [externalInjuries, setExternalInjuries] = useState(false);
  const [injuriesDescription, setInjuriesDescription] = useState('');

  // Allocation
  const [allocationLocation, setAllocationLocation] = useState('');
  const [bayNumber, setBayNumber] = useState('');

  // Media
  const [mediaFiles, setMediaFiles] = useState([]);
  const [gpsLatitude, setGpsLatitude] = useState('');
  const [gpsLongitude, setGpsLongitude] = useState('');

  // "Cadastrar novo" states
  const [creatingBase, setCreatingBase] = useState(false);
  const [newBaseName, setNewBaseName] = useState('');
  const [newBaseCity, setNewBaseCity] = useState('');
  const [newBaseState, setNewBaseState] = useState('');
  const [savingBase, setSavingBase] = useState(false);

  const [creatingOrgan, setCreatingOrgan] = useState(false);
  const [creatingAnimalGroup, setCreatingAnimalGroup] = useState(false);
  const [creatingConsciousness, setCreatingConsciousness] = useState(false);
  const [creatingHydration, setCreatingHydration] = useState(false);
  const [creatingAllocationLocation, setCreatingAllocationLocation] = useState(false);

  const organOptions = mergeCustomOption(DEFAULT_ORGAN_OPTIONS, responsibleOrgan);
  const animalGroupOptions = mergeCustomOption(DEFAULT_ANIMAL_GROUP_OPTIONS, animalGroup);
  const consciousnessOptions = mergeCustomOption(DEFAULT_CONSCIOUSNESS_OPTIONS, consciousness);
  const hydrationOptions = mergeCustomOption(DEFAULT_HYDRATION_OPTIONS, hydration);
  const allocationOptions = mergeCustomOption(DEFAULT_ALLOCATION_OPTIONS, allocationLocation);
  const selectedSpeciesCatalogItem = useMemo(
    () => speciesCatalog.find((item) => normalizeOptionText(item.scientific_name) === normalizeOptionText(species)) || null,
    [speciesCatalog, species]
  );
  const filteredSpeciesCatalog = useMemo(() => (
    speciesCatalog
      .filter((item) => !animalGroup || item.animal_group === animalGroup)
      .filter((item) => {
        const search = normalizeOptionText(species);
        return !search
          || normalizeOptionText(item.scientific_name).includes(search)
          || normalizeOptionText(item.common_name).includes(search);
      })
      .slice(0, 8)
  ), [speciesCatalog, animalGroup, species]);
  const isCreatingNewSpecies = Boolean(species) && !selectedSpeciesCatalogItem;
  const canEditCommonNameForNewSpecies = canManageSpeciesCatalog && isCreatingNewSpecies;

  useEffect(() => {
    fetchBases();
    fetchSpeciesCatalog();
    if (id) {
      fetchAdmission();
    }
    requestLocation();
  }, [id]);

  useEffect(() => {
    if (id || !baseId || !animalGroup || creatingAnimalGroup) {
      setIbamaPreview(null);
      return undefined;
    }

    const controller = new AbortController();
    setLoadingIbamaPreview(true);
    axios.get(`${API}/admissions/available-ibama-ids`, {
      params: { base_id: baseId, animal_group: animalGroup },
      signal: controller.signal,
    })
      .then((response) => {
        setIbamaPreview(response.data || null);
      })
      .catch((error) => {
        if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') return;
        setIbamaPreview(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingIbamaPreview(false);
        }
      });

    return () => controller.abort();
  }, [id, baseId, animalGroup, creatingAnimalGroup]);

  useEffect(() => {
    const reusableIds = ibamaPreview?.reusable_ids?.map((item) => item.ibama_id) || [];
    if (selectedReusableIbamaId && !reusableIds.includes(selectedReusableIbamaId)) {
      setSelectedReusableIbamaId('');
    }
  }, [ibamaPreview, selectedReusableIbamaId]);

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLatitude(position.coords.latitude.toString());
          setGpsLongitude(position.coords.longitude.toString());
          toast.success('Localização capturada!');
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error('Error fetching bases:', error);
    }
  };

  const fetchSpeciesCatalog = async () => {
    try {
      const response = await axios.get(`${API}/species-catalog`);
      setSpeciesCatalog(response.data || []);
    } catch (error) {
      console.error('Error fetching species catalog:', error);
    }
  };

  const handleBaseChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingBase(true);
      setBaseId('');
      return;
    }
    setCreatingBase(false);
    setBaseId(value);
  };

  const handleCreateBase = async () => {
    if (!newBaseName.trim() || !newBaseCity.trim() || !newBaseState.trim()) {
      toast.error('Preencha nome, cidade e estado da nova base');
      return;
    }

    setSavingBase(true);
    try {
      const response = await axios.post(`${API}/bases`, {
        name: newBaseName.trim(),
        city: newBaseCity.trim(),
        state: newBaseState.trim().toUpperCase(),
      });

      await fetchBases();
      setBaseId(response.data.id);
      setCreatingBase(false);
      setNewBaseName('');
      setNewBaseCity('');
      setNewBaseState('');
      toast.success('Nova base cadastrada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar nova base');
    } finally {
      setSavingBase(false);
    }
  };

  const handleOrganChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingOrgan(true);
      setResponsibleOrgan('');
      return;
    }
    setCreatingOrgan(false);
    setResponsibleOrgan(value);
  };

  const handleAnimalGroupChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingAnimalGroup(true);
      setAnimalGroup('');
      return;
    }
    setCreatingAnimalGroup(false);
    setAnimalGroup(value);
    const selectedItemStillMatches = selectedSpeciesCatalogItem?.animal_group === value;
    if (!selectedItemStillMatches && !id) {
      setSpecies('');
      setCommonName('');
    }
  };

  const applySpeciesCatalogItem = (item) => {
    setAnimalGroup(item.animal_group || '');
    setSpecies(item.scientific_name || '');
    setCommonName(item.common_name || '');
    setAnimalClass('');
  };

  const handleSpeciesChange = (value) => {
    setSpecies(value);
    const normalizedValue = normalizeOptionText(value);
    const matchedItem = speciesCatalog.find((item) => (
      normalizeOptionText(item.scientific_name) === normalizedValue
      || normalizeOptionText(item.common_name) === normalizedValue
    ));
    if (matchedItem) {
      applySpeciesCatalogItem(matchedItem);
      return;
    }
    if (!canManageSpeciesCatalog) {
      setCommonName('');
    }
  };

  const handleConsciousnessChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingConsciousness(true);
      setConsciousness('');
      return;
    }
    setCreatingConsciousness(false);
    setConsciousness(value);
  };

  const handleHydrationChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingHydration(true);
      setHydration('');
      return;
    }
    setCreatingHydration(false);
    setHydration(value);
  };

  const handleAllocationChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingAllocationLocation(true);
      setAllocationLocation('');
      return;
    }
    setCreatingAllocationLocation(false);
    setAllocationLocation(value);
  };

  const fetchAdmission = async () => {
    try {
      const response = await axios.get(`${API}/admissions/${id}`);
      setAdmission(response.data);
      // Populate form with existing data
      const adm = response.data;
      setBaseId(adm.base_id);
      setOriginCity(adm.origin_city);
      setOriginState(adm.origin_state || '');
      setOriginRegion(adm.origin_region || '');
      setOriginNeighborhood(adm.origin_neighborhood);
      setResponsibleName(adm.responsible.name);
      setResponsibleOrgan(adm.responsible.organ);
      setResponsibleCity(adm.responsible.city);
      // Load signature
      if (adm.responsible.signature_data) {
        setSignatureFile(adm.responsible.signature_data);
        setUseCanvas(adm.responsible.signature_type === 'canvas');
        // If canvas type, we need to redraw it after component mounts
        if (adm.responsible.signature_type === 'canvas') {
          setTimeout(() => {
            if (signatureRef.current) {
              const img = new Image();
              img.onload = () => {
                const canvas = signatureRef.current.getCanvas();
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
              };
              img.src = adm.responsible.signature_data;
            }
          }, 100);
        }
      }
      setAnimalGroup(adm.animal_group);
      setIbamaId(adm.ibama_id || '');
      const loadedAdmissionDate = toDateInputValue(adm.admission_date);
      const loadedAdmissionTime = toTimeInputValue(adm.admission_date);
      setAdmissionDate(loadedAdmissionDate);
      setAdmissionTime(loadedAdmissionTime);
      setOriginalAdmissionDate(loadedAdmissionDate);
      setOriginalAdmissionTime(loadedAdmissionTime);
      setAdmissionDateCorrectionJustification('');
      setSpecies(adm.species);
      setCommonName(adm.common_name);
      setAnimalClass(adm.animal_class || '');
      setPmpId(adm.pmp_id || '');
      setAdmittedDead(Boolean(adm.admitted_dead || adm.death_at_admission));
      
      if (adm.clinical_assessment) {
        const ca = adm.clinical_assessment;
        setConsciousness(ca.consciousness);
        setCardiacAuscultation(ca.cardiac_auscultation);
        setRespiratoryAuscultation(ca.respiratory_auscultation);
        setReflexes(ca.reflexes);
        setTemperature(ca.temperature.toString());
        setBodyScore(ca.body_score.toString());
        setHydration(ca.hydration);
        setOilContamination(ca.oil_contamination);
        setExternalInjuries(ca.external_injuries);
        setInjuriesDescription(ca.injuries_description || '');
      }
      
      setAllocationLocation(adm.allocation_location);
      setBayNumber(adm.bay_number);
      setMediaFiles(adm.media_files || []);
    } catch (error) {
      toast.error('Erro ao carregar admissão');
    }
  };


  // Redraw signature on canvas when switching tabs or when signature data changes
  useEffect(() => {
    if (useCanvas && signatureFile && signatureRef.current && currentTab === 'identification') {
      // Clear canvas first
      signatureRef.current.clear();
      
      // Redraw signature from stored data
      const img = new Image();
      img.onload = () => {
        const canvas = signatureRef.current.getCanvas();
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = signatureFile;
    }
  }, [currentTab, signatureFile, useCanvas]);


  const getSignatureData = () => {
    if (useCanvas && signatureRef.current) {
      // Check if canvas is not empty before returning data
      if (!signatureRef.current.isEmpty()) {
        return signatureRef.current.toDataURL();
      }
      return signatureFile || null;
    } else if (signatureFile) {
      return signatureFile;
    }
    return null;
  };

  const cacheCanvasSignature = () => {
    if (!useCanvas || !signatureRef.current || signatureRef.current.isEmpty()) {
      return;
    }
    setSignatureFile(signatureRef.current.toDataURL());
    setSignatureSaved(false);
  };

  const handleTabChange = (nextTab) => {
    if (currentTab === 'identification' && nextTab !== 'identification') {
      cacheCanvasSignature();
    }
    setCurrentTab(nextTab);
  };

  const handleSignatureFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureFile(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleSaveSignature = async () => {
    const signatureData = getSignatureData();
    if (!signatureData) {
      toast.error('Por favor, desenhe ou faça upload de uma assinatura primeiro');
      return;
    }

    // Always cache signature locally so it is not lost when changing tabs.
    setSignatureFile(signatureData);
    setSignatureSaved(true);

    if (!currentAdmissionId) {
      toast.success('Assinatura gravada no formulário. Ela será registrada ao salvar a admissão.');
      return;
    }

    setSavingSignature(true);
    try {
      await axios.patch(`${API}/admissions/${currentAdmissionId}/signature`, {
        signature_data: signatureData,
        signature_type: useCanvas ? 'canvas' : 'upload'
      });
      
      setSignatureSaved(true);
      toast.success('✅ Assinatura gravada e registrada com sucesso!');
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar assinatura');
    } finally {
      setSavingSignature(false);
    }
  };


  const handleSubmit = async () => {
    setLoading(true);
    try {
      const signatureData = getSignatureData();
      if (!signatureData) {
        toast.error('Por favor, adicione uma assinatura');
        setLoading(false);
        return;
      }

      const admissionDateChanged = Boolean(id)
        && (admissionDate !== originalAdmissionDate || admissionTime !== originalAdmissionTime);

      if (admissionDateChanged && !admissionDateCorrectionJustification.trim()) {
        toast.error('Informe a justificativa para corrigir a data de entrada');
        setLoading(false);
        return;
      }

      if (isCreatingNewSpecies && !canManageSpeciesCatalog) {
        toast.error('Selecione uma espécie do catálogo antes de salvar. Cadastros novos são restritos à administração.');
        handleTabChange('animal');
        setLoading(false);
        return;
      }

      if (isCreatingNewSpecies && canManageSpeciesCatalog && !commonName.trim()) {
        toast.error('Informe o nome popular para cadastrar a nova espécie');
        setLoading(false);
        return;
      }

      const parsedTemperature = Number.parseFloat(temperature);
      const hasClinicalAssessment = [
        consciousness,
        cardiacAuscultation,
        respiratoryAuscultation,
        reflexes,
        temperature,
        bodyScore,
        hydration,
        injuriesDescription,
      ].some(isFilled) || oilContamination || externalInjuries;

      let clinicalAssessment = null;
      if (hasClinicalAssessment) {
        const missingClinicalFields = [
          [consciousness, 'consciência'],
          [cardiacAuscultation, 'ausculta cardíaca'],
          [respiratoryAuscultation, 'ausculta respiratória'],
          [reflexes, 'reflexos'],
          [Number.isFinite(parsedTemperature) ? temperature : '', 'temperatura'],
          [bodyScore, 'escore corporal'],
          [hydration, 'hidratação'],
        ]
          .filter(([value]) => !isFilled(value))
          .map(([, label]) => label);

        if (missingClinicalFields.length > 0) {
          toast.error(`Preencha a avaliação clínica completa: ${missingClinicalFields.join(', ')}`);
          setLoading(false);
          return;
        }

        clinicalAssessment = {
          consciousness,
          cardiac_auscultation: cardiacAuscultation,
          respiratory_auscultation: respiratoryAuscultation,
          reflexes,
          temperature: parsedTemperature,
          body_score: bodyScore,
          hydration,
          oil_contamination: oilContamination,
          external_injuries: externalInjuries,
          injuries_description: injuriesDescription || null,
        };
      }

      const data = {
        base_id: baseId,
        origin_city: originCity,
        origin_state: originState || null,
        origin_region: originRegion || null,
        origin_neighborhood: originNeighborhood,
        responsible: {
          name: responsibleName,
          organ: responsibleOrgan,
          city: responsibleCity,
          signature_type: useCanvas ? 'canvas' : 'upload',
          signature_data: signatureData
        },
        animal_group: animalGroup,
        species: species,
        common_name: commonName,
        animal_class: null,
        pmp_id: pmpId || null,
        admitted_dead: admittedDead,
        clinical_assessment: clinicalAssessment,
        allocation_location: allocationLocation,
        bay_number: bayNumber
      };

      if (!id || admissionDateChanged) {
        data.admission_date = buildDateTimeIso(admissionDate, admissionTime);
      }

      if (admissionDateChanged) {
        data.admission_date_correction_justification = admissionDateCorrectionJustification.trim();
      }

      if (id && canCorrectAdmissionAdminFields) {
        data.ibama_id = ibamaId;
      }

      if (!id && selectedReusableIbamaId) {
        data.preferred_ibama_id = selectedReusableIbamaId;
      }

      if (id) {
        // Update
        await axios.put(`${API}/admissions/${id}`, data);
        toast.success('Admissão atualizada!');
      } else {
        // Create
        const response = await axios.post(`${API}/admissions`, data);
        const newAdmissionId = response.data.id;
        setCurrentAdmissionId(newAdmissionId);
        toast.success('Admissão criada com sucesso!');
      }
      
      navigate('/');
    } catch (error) {
      console.error('Error saving admission:', error);
      toast.error(getApiErrorMessage(error, 'Erro ao salvar admissão'));
    } finally {
      setLoading(false);
    }
  };

  const handleMediaUpload = async (file) => {
    if (!file || !id) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', file.type.startsWith('image/') ? 'photo' : 'video');
    if (gpsLatitude) formData.append('gps_latitude', gpsLatitude);
    if (gpsLongitude) formData.append('gps_longitude', gpsLongitude);

    try {
      await axios.post(`${API}/admissions/${id}/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Mídia enviada!');
      fetchAdmission();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao enviar mídia'));
    }
  };

  const handleDeleteMedia = async (media) => {
    if (!id || !media?.file_url) return;
    if (!window.confirm('Excluir esta mídia anexada?')) return;
    try {
      await axios.delete(`${API}/admissions/${id}/media`, {
        params: { file_url: media.file_url }
      });
      toast.success('Mídia excluída');
      fetchAdmission();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao excluir mídia'));
    }
  };

  const handleOpenAdmissionMedia = async (event, media) => {
    event.preventDefault();
    try {
      await openProtectedFile(media.file_url, media.filename);
    } catch (error) {
      toast.error(error.message || 'Não foi possível abrir o arquivo');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}>
                <h1 className="text-xl font-bold text-emerald-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {id ? 'Editar Admissão' : 'Nova Admissão'}
                </h1>
                {SHOW_INSTITUTO_BW_BRANDING && (
                  <p className="text-xs text-emerald-500">Instituto BW - Sistema CRAS</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                data-testid="back-button"
              >
                Voltar
              </Button>
              <Button onClick={logout} variant="ghost" className="text-red-600 hover:bg-red-50">
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-8">
        <Card className="shadow-lg border-emerald-100">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <Tabs value={currentTab} onValueChange={handleTabChange}>
              {/* Mobile: Dropdown Select */}
              <div className="block md:hidden mb-4">
                <Label className="text-base font-semibold text-emerald-800 mb-2 block">
                  Seção do Formulário
                </Label>
                <Select value={currentTab} onValueChange={handleTabChange}>
                  <SelectTrigger className="w-full min-h-[48px] text-base font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="identification" className="text-base py-3">
                      📋 Identificação
                    </SelectItem>
                    <SelectItem value="animal" className="text-base py-3">
                      🦜 Animal
                    </SelectItem>
                    <SelectItem value="clinical" className="text-base py-3">
                      🏥 Avaliação Clínica
                    </SelectItem>
                    <SelectItem value="allocation" className="text-base py-3">
                      🏠 Alocação
                    </SelectItem>
                    <SelectItem value="media" className="text-base py-3">
                      📸 Mídia
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Desktop: Tab Grid */}
              <div className="hidden md:block">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="identification" data-testid="tab-identification" className="text-sm md:text-base">
                    Identificação
                  </TabsTrigger>
                  <TabsTrigger value="animal" data-testid="tab-animal" className="text-sm md:text-base">
                    Animal
                  </TabsTrigger>
                  <TabsTrigger value="clinical" data-testid="tab-clinical" className="text-sm md:text-base">
                    Avaliação
                  </TabsTrigger>
                  <TabsTrigger value="allocation" data-testid="tab-allocation" className="text-sm md:text-base">
                    Alocação
                  </TabsTrigger>
                  <TabsTrigger value="media" data-testid="tab-media" className="text-sm md:text-base">
                    Mídia
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Tab 1: Identification */}
              <TabsContent value="identification" className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Base *</Label>
                    <Select value={creatingBase ? CREATE_NEW_VALUE : baseId} onValueChange={handleBaseChange} disabled={!!id}>
                      <SelectTrigger data-testid="base-select">
                        <SelectValue placeholder="Selecione a base" />
                      </SelectTrigger>
                      <SelectContent>
                        {bases.map((base) => (
                          <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                        ))}
                        <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar nova base</SelectItem>
                      </SelectContent>
                    </Select>
                    {creatingBase && !id && (
                      <div className="mt-3 border rounded-md p-3 bg-emerald-50 space-y-2">
                        <Input
                          placeholder="Nome da nova base"
                          value={newBaseName}
                          onChange={(e) => setNewBaseName(e.target.value)}
                          data-testid="new-base-name-input"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <Input
                            placeholder="Cidade"
                            value={newBaseCity}
                            onChange={(e) => setNewBaseCity(e.target.value)}
                            data-testid="new-base-city-input"
                          />
                          <Input
                            placeholder="UF (ex: SP)"
                            value={newBaseState}
                            onChange={(e) => setNewBaseState(e.target.value)}
                            maxLength={2}
                            data-testid="new-base-state-input"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={handleCreateBase}
                          disabled={savingBase}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="create-base-button"
                        >
                          {savingBase ? 'Cadastrando base...' : 'Cadastrar base'}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Data de Entrada *</Label>
                    <Input
                      type="date"
                      value={admissionDate}
                      onChange={(e) => setAdmissionDate(e.target.value)}
                      disabled={!!id && !canCorrectAdmissionAdminFields}
                      required
                      data-testid="admission-date-input"
                    />
                  </div>
                  <div>
                    <Label>Horário de Entrada *</Label>
                    <Input
                      type="time"
                      value={admissionTime}
                      onChange={(e) => setAdmissionTime(e.target.value)}
                      disabled={!!id && !canCorrectAdmissionAdminFields}
                      required
                      data-testid="admission-time-input"
                    />
                  </div>
                  {id && canCorrectAdmissionAdminFields && (admissionDate !== originalAdmissionDate || admissionTime !== originalAdmissionTime) && (
                    <div className="md:col-span-2">
                      <Label>Justificativa da correção da data de entrada *</Label>
                      <Textarea
                        value={admissionDateCorrectionJustification}
                        onChange={(e) => setAdmissionDateCorrectionJustification(e.target.value)}
                        placeholder="Descreva o motivo da correção da data e horário de entrada"
                        required
                        data-testid="admission-date-correction-justification"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Cidade de Origem *</Label>
                    <Input value={originCity} onChange={(e) => setOriginCity(e.target.value)} data-testid="origin-city-input" />
                  </div>
                  <div>
                    <Label>Estado de Origem</Label>
                    <Input value={originState} onChange={(e) => setOriginState(e.target.value.toUpperCase())} maxLength={2} data-testid="origin-state-input" />
                  </div>
                  <div>
                    <Label>Região de Origem</Label>
                    <Input value={originRegion} onChange={(e) => setOriginRegion(e.target.value)} placeholder="Ex: Litoral Norte" data-testid="origin-region-input" />
                  </div>
                  <div>
                    <Label>Bairro de Origem *</Label>
                    <Input value={originNeighborhood} onChange={(e) => setOriginNeighborhood(e.target.value)} data-testid="origin-neighborhood-input" />
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-emerald-800 mb-4">Responsável pela Entrega</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome Completo *</Label>
                      <Input value={responsibleName} onChange={(e) => setResponsibleName(e.target.value)} data-testid="responsible-name-input" />
                    </div>
                  <div>
                    <Label>Órgão *</Label>
                      <Select value={creatingOrgan ? CREATE_NEW_VALUE : responsibleOrgan} onValueChange={handleOrganChange}>
                        <SelectTrigger data-testid="responsible-organ-select">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {organOptions.map((organ) => (
                            <SelectItem key={organ} value={organ}>{organ}</SelectItem>
                          ))}
                          <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo órgão</SelectItem>
                        </SelectContent>
                      </Select>
                      {creatingOrgan && (
                        <Input
                          className="mt-2"
                          placeholder="Digite o novo órgão"
                          value={responsibleOrgan}
                          onChange={(e) => setResponsibleOrgan(e.target.value)}
                          data-testid="new-responsible-organ-input"
                        />
                      )}
                    </div>
                    <div>
                      <Label>Cidade *</Label>
                      <Input value={responsibleCity} onChange={(e) => setResponsibleCity(e.target.value)} data-testid="responsible-city-input" />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label>Assinatura Digital *</Label>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3">
                      <Button
                        type="button"
                        variant={useCanvas ? 'default' : 'outline'}
                        onClick={() => setUseCanvas(true)}
                        className={`w-full sm:w-auto min-h-[44px] ${useCanvas ? 'bg-emerald-600' : ''}`}
                        data-testid="signature-canvas-button"
                      >
                        Desenhar
                      </Button>
                      <Button
                        type="button"
                        variant={!useCanvas ? 'default' : 'outline'}
                        onClick={() => setUseCanvas(false)}
                        className={`w-full sm:w-auto min-h-[44px] ${!useCanvas ? 'bg-emerald-600' : ''}`}
                        data-testid="signature-upload-button"
                      >
                        Upload
                      </Button>
                    </div>
                    {useCanvas ? (
                      <div>
                        <div className="border-2 border-emerald-300 rounded-md overflow-hidden w-full">
                          <SignatureCanvas
                            ref={signatureRef}
                            canvasProps={{ 
                              width: typeof window !== 'undefined' && window.innerWidth < 640 ? window.innerWidth - 80 : 500,
                              height: 200,
                              className: 'signature-canvas w-full touch-none'
                            }}
                            onEnd={cacheCanvasSignature}
                          />
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3">
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full sm:w-auto min-h-[44px]"
                            onClick={() => {
                              signatureRef.current?.clear();
                              setSignatureSaved(false);
                              setSignatureFile(null);
                            }}
                            data-testid="clear-signature-button"
                          >
                            Limpar
                          </Button>
                          <Button
                            type="button"
                            variant="default"
                            className="w-full sm:w-auto min-h-[44px] bg-emerald-600 hover:bg-emerald-700 font-semibold"
                            onClick={handleSaveSignature}
                            disabled={savingSignature}
                            data-testid="save-signature-button"
                          >
                            {savingSignature ? 'Salvando...' : signatureSaved ? '✓ Assinatura Gravada' : 'Gravar Assinatura'}
                          </Button>
                        </div>
                        {signatureSaved && (
                          <p className="text-sm text-emerald-600 mt-3 p-3 bg-emerald-50 rounded-md font-medium">
                            ✓ Assinatura gravada! Você pode trocar de aba sem perder os dados.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleSignatureFileChange}
                          className="min-h-[44px]"
                          data-testid="signature-file-input"
                        />
                        {signatureFile && (
                          <div>
                            <img src={signatureFile} alt="Signature" className="mt-3 max-h-32 border rounded w-full object-contain" />
                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              onClick={handleSaveSignature}
                              disabled={savingSignature}
                              className="mt-2 bg-emerald-600 hover:bg-emerald-700"
                              data-testid="save-signature-upload-button"
                            >
                              {savingSignature ? 'Salvando...' : signatureSaved ? '✓ Assinatura Gravada' : 'Gravar Assinatura'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button 
                    onClick={() => {
                      handleTabChange('animal');
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[48px] text-base font-semibold" 
                    data-testid="next-to-animal-button"
                  >
                    Próximo: Animal →
                  </Button>
                </div>
              </TabsContent>

              {/* Tab 2: Animal */}
              <TabsContent value="animal" className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {id && (
                    <div>
                      <Label>ID IBAMA</Label>
                      <Input
                        value={ibamaId}
                        onChange={(e) => setIbamaId(e.target.value)}
                        disabled={!canCorrectAdmissionAdminFields}
                        data-testid="ibama-id-input"
                      />
                      {!canCorrectAdmissionAdminFields && (
                        <p className="mt-1 text-xs text-gray-500">Correção restrita a administrador ou programador.</p>
                      )}
                    </div>
                  )}
                  <div>
                    <Label>Grupo *</Label>
                    <Select value={creatingAnimalGroup ? CREATE_NEW_VALUE : animalGroup} onValueChange={handleAnimalGroupChange} disabled={!!id}>
                      <SelectTrigger data-testid="animal-group-select">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {animalGroupOptions.map((group) => (
                          <SelectItem key={group} value={group}>{group}</SelectItem>
                        ))}
                        <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo grupo</SelectItem>
                      </SelectContent>
                    </Select>
                    {creatingAnimalGroup && (
                      <Input
                        className="mt-2"
                        placeholder="Digite o novo grupo (ex: ANF)"
                        value={animalGroup}
                        onChange={(e) => setAnimalGroup(e.target.value.toUpperCase())}
                        data-testid="new-animal-group-input"
                      />
                    )}
                  </div>
                  {!id && baseId && animalGroup && !creatingAnimalGroup && (
                    <div className="md:col-span-2 rounded-md border border-emerald-200 bg-emerald-50 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <Label className="text-emerald-900">Número IBAMA previsto</Label>
                          <p className="mt-1 text-2xl font-bold text-emerald-800" data-testid="ibama-preview-value">
                            {loadingIbamaPreview ? 'Consultando...' : (selectedReusableIbamaId || ibamaPreview?.generated_next_id || 'Ainda não disponível')}
                          </p>
                          <p className="mt-1 text-sm text-emerald-700">
                            O número será confirmado ao salvar a admissão.
                          </p>
                        </div>
                        {(ibamaPreview?.reusable_ids || []).length > 0 && (
                          <div className="w-full md:w-72">
                            <Label>Usar número disponível</Label>
                            <Select value={selectedReusableIbamaId || '__sequencial__'} onValueChange={(value) => setSelectedReusableIbamaId(value === '__sequencial__' ? '' : value)}>
                              <SelectTrigger data-testid="reusable-ibama-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__sequencial__">Usar próximo sequencial</SelectItem>
                                {ibamaPreview.reusable_ids.map((item) => (
                                  <SelectItem key={item.ibama_id} value={item.ibama_id}>
                                    {item.ibama_id}{item.common_name ? ` • ${item.common_name}` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Espécie *</Label>
                    <Input
                      list="species-catalog-options"
                      value={species}
                      onChange={(e) => handleSpeciesChange(e.target.value)}
                      placeholder="Busque pelo nome científico"
                      data-testid="species-input"
                    />
                    <datalist id="species-catalog-options">
                      {speciesCatalog
                        .filter((item) => !animalGroup || item.animal_group === animalGroup)
                        .map((item) => (
                          <option key={item.id} value={item.scientific_name}>
                            {item.common_name}
                          </option>
                        ))}
                    </datalist>
                    {filteredSpeciesCatalog.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {filteredSpeciesCatalog.map((item) => (
                          <Button
                            key={item.id}
                            type="button"
                            variant={item.scientific_name === species ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => applySpeciesCatalogItem(item)}
                            className={item.scientific_name === species ? 'bg-emerald-600' : ''}
                          >
                            {item.scientific_name}
                          </Button>
                        ))}
                      </div>
                    )}
                    {isCreatingNewSpecies && (
                      <p className={`mt-1 text-xs ${canManageSpeciesCatalog ? 'text-amber-700' : 'text-red-600'}`}>
                        {canManageSpeciesCatalog
                          ? 'Nova espécie: será cadastrada no catálogo ao salvar.'
                          : 'Espécie não encontrada no catálogo. Solicite o cadastro a um administrador.'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Nome Popular *</Label>
                    <Input
                      value={commonName}
                      onChange={(e) => setCommonName(e.target.value)}
                      disabled={!canEditCommonNameForNewSpecies}
                      placeholder={selectedSpeciesCatalogItem ? 'Preenchido pelo catálogo' : 'Selecione uma espécie'}
                      data-testid="common-name-input"
                    />
                    {!canEditCommonNameForNewSpecies && (
                      <p className="mt-1 text-xs text-gray-500">Preenchido automaticamente pelo catálogo de espécies.</p>
                    )}
                  </div>
                  <div>
                    <Label>Número PMP</Label>
                    <Input
                      value={pmpId}
                      onChange={(e) => setPmpId(e.target.value)}
                      placeholder="Obrigatório para animais marinhos monitorados"
                      data-testid="pmp-id-input"
                    />
                  </div>
                </div>

                <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="admitted-dead"
                      checked={admittedDead}
                      onCheckedChange={(checked) => setAdmittedDead(Boolean(checked))}
                      disabled={!!id}
                      data-testid="admitted-dead-checkbox"
                    />
                    <div className="space-y-1">
                      <Label htmlFor="admitted-dead" className="cursor-pointer font-semibold text-gray-800">
                        Animal admitido já em óbito
                      </Label>
                      <p className="text-sm text-gray-600">
                        Ao salvar, o animal será cadastrado com desfecho de óbito, acompanhamento encerrado e aba de necrópsia liberada.
                      </p>
                    </div>
                  </div>
                </div>

                {id && admission && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                    <p className="text-emerald-800 font-semibold">ID IBAMA: {admission.ibama_id}</p>
                    {admission.pmp_id && <p className="text-emerald-700 mt-1">PMP: {admission.pmp_id}</p>}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
                  <Button 
                    onClick={() => handleTabChange('identification')}
                    variant="outline" 
                    className="w-full sm:w-auto min-h-[48px] text-base order-2 sm:order-1" 
                    data-testid="back-to-identification-button"
                  >
                    ← Voltar
                  </Button>
                  <Button 
                    onClick={() => handleTabChange('clinical')}
                    className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[48px] text-base font-semibold order-1 sm:order-2" 
                    data-testid="next-to-clinical-button"
                  >
                    Próximo: Avaliação →
                  </Button>
                </div>
              </TabsContent>

              {/* Tab 3: Clinical Assessment */}
              <TabsContent value="clinical" className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Consciência</Label>
                    <Select value={creatingConsciousness ? CREATE_NEW_VALUE : consciousness} onValueChange={handleConsciousnessChange}>
                      <SelectTrigger data-testid="consciousness-select">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {consciousnessOptions.map((item) => (
                          <SelectItem key={item} value={item}>{item}</SelectItem>
                        ))}
                        <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar nova consciência</SelectItem>
                      </SelectContent>
                    </Select>
                    {creatingConsciousness && (
                      <Input
                        className="mt-2"
                        placeholder="Digite a nova opção de consciência"
                        value={consciousness}
                        onChange={(e) => setConsciousness(e.target.value)}
                        data-testid="new-consciousness-input"
                      />
                    )}
                  </div>
                  <div>
                    <Label>Ausculta Cardíaca</Label>
                    <Input value={cardiacAuscultation} onChange={(e) => setCardiacAuscultation(e.target.value)} data-testid="cardiac-input" />
                  </div>
                  <div>
                    <Label>Ausculta Respiratória</Label>
                    <Input value={respiratoryAuscultation} onChange={(e) => setRespiratoryAuscultation(e.target.value)} data-testid="respiratory-input" />
                  </div>
                  <div>
                    <Label>Reflexos</Label>
                    <Input value={reflexes} onChange={(e) => setReflexes(e.target.value)} data-testid="reflexes-input" />
                  </div>
                  <div>
                    <Label>Temperatura (°C)</Label>
                    <Input type="number" step="0.1" value={temperature} onChange={(e) => setTemperature(e.target.value)} data-testid="temperature-input" />
                  </div>
                  <div>
                    <Label>Escore Corporal (1-5)</Label>
                    <Input type="number" min="1" max="5" value={bodyScore} onChange={(e) => setBodyScore(e.target.value)} data-testid="body-score-input" />
                  </div>
                  <div>
                    <Label>Hidratação</Label>
                    <Select value={creatingHydration ? CREATE_NEW_VALUE : hydration} onValueChange={handleHydrationChange}>
                      <SelectTrigger data-testid="hydration-select">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {hydrationOptions.map((item) => (
                          <SelectItem key={item} value={item}>{item}</SelectItem>
                        ))}
                        <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar nova hidratação</SelectItem>
                      </SelectContent>
                    </Select>
                    {creatingHydration && (
                      <Input
                        className="mt-2"
                        placeholder="Digite a nova opção de hidratação"
                        value={hydration}
                        onChange={(e) => setHydration(e.target.value)}
                        data-testid="new-hydration-input"
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={oilContamination}
                      onCheckedChange={setOilContamination}
                      id="oil-contamination"
                      data-testid="oil-contamination-checkbox"
                    />
                    <Label htmlFor="oil-contamination">Contaminação por óleo</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={externalInjuries}
                      onCheckedChange={setExternalInjuries}
                      id="external-injuries"
                      data-testid="external-injuries-checkbox"
                    />
                    <Label htmlFor="external-injuries">Lesões externas</Label>
                  </div>
                  {externalInjuries && (
                    <div>
                      <Label>Descrição das lesões</Label>
                      <Textarea
                        value={injuriesDescription}
                        onChange={(e) => setInjuriesDescription(e.target.value)}
                        data-testid="injuries-description-textarea"
                      />
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
                  <Button 
                    onClick={() => handleTabChange('animal')}
                    variant="outline" 
                    className="w-full sm:w-auto min-h-[48px] text-base order-2 sm:order-1" 
                    data-testid="back-to-animal-button"
                  >
                    ← Voltar
                  </Button>
                  <Button 
                    onClick={() => handleTabChange('allocation')}
                    className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[48px] text-base font-semibold order-1 sm:order-2" 
                    data-testid="next-to-allocation-button"
                  >
                    Próximo: Alocação →
                  </Button>
                </div>
              </TabsContent>

              {/* Tab 4: Allocation */}
              <TabsContent value="allocation" className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Local de Alocação *</Label>
                    <Select value={creatingAllocationLocation ? CREATE_NEW_VALUE : allocationLocation} onValueChange={handleAllocationChange}>
                      <SelectTrigger data-testid="allocation-location-select">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {allocationOptions.map((item) => (
                          <SelectItem key={item} value={item}>{item}</SelectItem>
                        ))}
                        <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo local</SelectItem>
                      </SelectContent>
                    </Select>
                    {creatingAllocationLocation && (
                      <Input
                        className="mt-2"
                        placeholder="Digite o novo local de alocação"
                        value={allocationLocation}
                        onChange={(e) => setAllocationLocation(e.target.value)}
                        data-testid="new-allocation-location-input"
                      />
                    )}
                  </div>
                  <div>
                    <Label>Número da Baía *</Label>
                    <Input value={bayNumber} onChange={(e) => setBayNumber(e.target.value)} data-testid="bay-number-input" />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
                  <Button 
                    onClick={() => handleTabChange('clinical')}
                    variant="outline" 
                    className="w-full sm:w-auto min-h-[48px] text-base order-2 sm:order-1" 
                    data-testid="back-to-clinical-button"
                  >
                    ← Voltar
                  </Button>
                  <Button 
                    onClick={() => handleTabChange('media')}
                    className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[48px] text-base font-semibold order-1 sm:order-2" 
                    data-testid="next-to-media-button"
                  >
                    Próximo: Mídia →
                  </Button>
                </div>
              </TabsContent>

              {/* Tab 5: Media */}
              <TabsContent value="media" className="space-y-4 mt-6">
                {id ? (
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-800 mb-4">Upload de Fotos e Vídeos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Latitude GPS</Label>
                        <Input value={gpsLatitude} onChange={(e) => setGpsLatitude(e.target.value)} data-testid="gps-latitude-input" placeholder="Auto-capturado" />
                      </div>
                      <div>
                        <Label>Longitude GPS</Label>
                        <Input value={gpsLongitude} onChange={(e) => setGpsLongitude(e.target.value)} data-testid="gps-longitude-input" placeholder="Auto-capturado" />
                      </div>
                    </div>
                    <CameraFileInput
                      accept="image/*,video/*"
                      onFileSelected={handleMediaUpload}
                      fileLabel="Selecionar foto/vídeo"
                      cameraLabel="Tirar foto"
                      data-testid="media-upload-input"
                    />
                    
                    {mediaFiles.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2">Arquivos enviados:</h4>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {mediaFiles.map((media, idx) => (
                            <div key={media.file_url || idx} className="flex items-start justify-between gap-2 border rounded p-2 text-sm">
                              <button
                                type="button"
                                className="flex min-w-0 flex-1 items-start gap-2 text-left"
                                onClick={(event) => handleOpenAdmissionMedia(event, media)}
                              >
                                <ProtectedMediaThumbnail
                                  media={media}
                                  alt={media.caption || media.filename || `Mídia ${idx + 1}`}
                                  className="h-16 w-20"
                                  fallbackClassName="h-16 w-20"
                                />
                                <span className="min-w-0">
                                  <span className="block truncate font-medium">{media.caption || media.filename}</span>
                                  {media.gps_latitude && <span className="block truncate text-xs text-gray-500">GPS: {media.gps_latitude}, {media.gps_longitude}</span>}
                                  {media.uploaded_at && <span className="block truncate text-xs text-gray-500">{new Date(media.uploaded_at).toLocaleString('pt-BR')}</span>}
                                </span>
                              </button>
                              {['administrador', 'programador'].includes(user?.role) && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2 text-red-600"
                                  onClick={() => handleDeleteMedia(media)}
                                  title="Excluir mídia"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Salve a admissão primeiro para fazer upload de mídia</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
                  <Button 
                    onClick={() => handleTabChange('allocation')}
                    variant="outline" 
                    className="w-full sm:w-auto min-h-[48px] text-base order-2 sm:order-1" 
                    data-testid="back-to-allocation-button"
                  >
                    ← Voltar
                  </Button>
                  <Button 
                    onClick={handleSubmit} 
                    className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[52px] text-base font-bold order-1 sm:order-2" 
                    disabled={loading} 
                    data-testid="submit-admission-button"
                  >
                    {loading ? 'Salvando...' : (id ? 'Atualizar Admissão' : '✓ Criar Admissão')}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
